import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDisclosure } from '@chakra-ui/react';

import { normalizeForSearch } from 'utils/CommonFunction';

const useMerchantFilter = (
  selectedMerchant,
  handleSelectChange,
  emptyText = 'All merchants',
) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingSelectedMerchant, setPendingSelectedMerchant] = useState(
    selectedMerchant || [],
  );

  const {
    isOpen: isMerchantPopoverOpen,
    onOpen: openMerchantPopover,
    onClose: closeMerchantPopover,
  } = useDisclosure();

  // Sync pending state when selectedMerchant prop changes
  useEffect(() => {
    setPendingSelectedMerchant(selectedMerchant || []);
  }, [selectedMerchant]);

  // Filter merchants based on search term (accent-insensitive)
  const getFilteredMerchants = (merchantNameList) => {
    const normalizedTerm = normalizeForSearch(searchTerm);
    return (
      merchantNameList?.filter((merchant) =>
        normalizeForSearch(merchant.name).includes(normalizedTerm),
      ) || []
    );
  };

  // Helper functions for multiselect optimization
  const handleLocalSelectChange = (newSelection) => {
    setPendingSelectedMerchant(newSelection);
  };

  const handleApplySelection = () => {
    handleSelectChange(pendingSelectedMerchant);
    closeMerchantPopover();
  };

  const handleCancelSelection = () => {
    setPendingSelectedMerchant(selectedMerchant || []);
    closeMerchantPopover();
  };

  const handleSelectAll = (filteredMerchants) => {
    if (
      pendingSelectedMerchant &&
      pendingSelectedMerchant.length === filteredMerchants.length
    ) {
      handleLocalSelectChange([]);
    } else {
      handleLocalSelectChange(filteredMerchants || []);
    }
  };

  const handleMerchantToggle = (merchant, isChecked) => {
    if (isChecked) {
      handleLocalSelectChange([...(pendingSelectedMerchant || []), merchant]);
    } else {
      handleLocalSelectChange(
        pendingSelectedMerchant?.filter(
          (m) => m.merchantId !== merchant.merchantId,
        ) || [],
      );
    }
  };

  const checkedMerchantIdSet = useMemo(
    () => new Set(pendingSelectedMerchant?.map((m) => m.merchantId) ?? []),
    [pendingSelectedMerchant],
  );

  const isSelectAllChecked = useCallback(
    (filteredMerchants) => {
      return (
        pendingSelectedMerchant &&
        pendingSelectedMerchant.length === filteredMerchants?.length
      );
    },
    [pendingSelectedMerchant],
  );

  const isMerchantChecked = useCallback(
    (merchant) => checkedMerchantIdSet.has(merchant.merchantId),
    [checkedMerchantIdSet],
  );

  const getSelectedMerchantText = () => {
    if (selectedMerchant && selectedMerchant.length > 0) {
      return selectedMerchant.length === 1
        ? selectedMerchant[0].name
        : `${selectedMerchant.length} merchants selected`;
    }
    return emptyText;
  };

  const handleClearSelection = () => {
    handleLocalSelectChange([]);
  };

  return {
    searchTerm,
    setSearchTerm,
    pendingSelectedMerchant,
    setPendingSelectedMerchant,
    isMerchantPopoverOpen,
    openMerchantPopover,
    closeMerchantPopover,
    getFilteredMerchants,
    handleLocalSelectChange,
    handleApplySelection,
    handleCancelSelection,
    handleSelectAll,
    handleMerchantToggle,
    isSelectAllChecked,
    isMerchantChecked,
    getSelectedMerchantText,
    handleClearSelection,
  };
};

export default useMerchantFilter;
